import { Component, inject, signal } from "@angular/core";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { ConfiguredProductComponent } from "@project/floorplanner/floorplan-menu/fgds/configured-product/configured-product.component";
import { FloorplanProductMenuComponent } from "@project/floorplanner/floorplan-menu/fgds/floorplan-product-menu/floorplan-product-menu.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { DraggedItemType, FloorplanDragDropService } from "@utils/floorplan-drag-drop.service";
import { take } from "rxjs/operators";

@Component({
  selector: "app-telemetry-menu",
  imports: [ConfiguredProductComponent, FloorplanProductMenuComponent],
  templateUrl: "./telemetry-menu.component.html",
  styleUrl: "./telemetry-menu.component.scss",
})
export class TelemetryMenuComponent {
  private floorplanService = inject(FloorplanService);
  private dragDropService = inject(FloorplanDragDropService);
  private monitoringProductService = inject(MonitoringProductService);
  private productService = inject(ProductService);

  protected floorplan: Floorplan;

  protected readonly tooltipTextTelemetry: string = $localize`:@@global.telemetry:Telemetry`;
  protected readonly fallbackImageUrl = "";

  private readonly pendingLoads = new Set<string>();
  protected readonly imageUrls = signal<ReadonlyMap<string, string>>(new Map());

  constructor() {
    this.floorplan = this.floorplanService.selectedFloorplan;
  }

  protected get telemetries(): readonly TelemetryConfiguration[] {
    if (this.floorplan.project instanceof MonitoringProject) {
      return this.floorplan.project.telemetries;
    }
    return [];
  }

  protected addTelemetry(telemetryConfig: TelemetryConfiguration) {
    this.floorplanService.addTelemetry(telemetryConfig);
  }

  protected addPlaceholder() {
    this.floorplanService.addTelemetryPlaceholder();
  }

  protected deleteTelemetry(telemetryConfig: TelemetryConfiguration) {
    (this.floorplan.project as MonitoringProject).deleteTelemetry(telemetryConfig);
  }

  protected drag(telemetryConfig: TelemetryConfiguration) {
    this.dragDropService.setDragged({ type: DraggedItemType.TELEMETRY, config: telemetryConfig });
  }

  protected getImageUrl(config: TelemetryConfiguration): string {
    const cached = this.imageUrls().get(config.id);
    if (cached !== undefined) return cached;

    if (!this.pendingLoads.has(config.id)) {
      this.pendingLoads.add(config.id);
      queueMicrotask(() => this.resolveImageUrl(config));
    }
    return "";
  }

  private resolveImageUrl(config: TelemetryConfiguration): void {
    const deviceId = config.family?.[0]?.option.devices[0];
    if (!deviceId) {
      this.setImageUrl(config.id, "");
      this.pendingLoads.delete(config.id);
      return;
    }

    this.monitoringProductService
      .loadDeviceFromDB(deviceId, MonitoringCategory.TELEMETRY, this.productService.getFilterCountry())
      .pipe(take(1))
      .subscribe((device) => {
        const url = device?.productImageCantoID ? ProductImage.getCantoSourceUrl(device.productImageCantoID) : "";
        this.setImageUrl(config.id, url);
        this.pendingLoads.delete(config.id);
      });
  }

  private setImageUrl(configId: string, url: string): void {
    this.imageUrls.update((map) => {
      const newMap = new Map(map);
      newMap.set(configId, url);
      return newMap;
    });
  }

  protected dragPlaceholder() {
    this.dragDropService.setDragged({ type: DraggedItemType.TELEMETRY_PLACEHOLDER });
  }

  protected updateTelemetriesItemTextVisibility(visible: boolean) {
    this.floorplan.floorplanState.updateTelemetriesItemTextVisibility(visible);
  }
}
